import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import Card from 'components/Card';
import CardContent from 'components/CardContent';
import PropTypes from 'prop-types';
import { isMobile } from 'helpers';
import hoverScrollbar, { hoverScrollbarVisible } from 'components/hoverScrollbar';

const dragThreshold = 5;
const pageRatio = 0.8;

const Viewport = styled('div')`
  position: relative;
`;

const ItemContainer = styled('div')`
  display: flex;
  flex-wrap: nowrap;
  width: 100%;
  max-width: 100vw;

  padding: 1rem 0;
  margin-top: -1rem;
  margin-bottom: -1rem;

  & > * + * {
    /* Space between elements */
    margin-left: 1rem;
  }

  ${({ hasOverflow, card, atCardBottom, theme }) => hasOverflow && css`
    /* Escape actual padding */
    margin-left: -1rem;
    width: calc(100% + 2rem);

    /* Keeps the scrollbar inside the box, or the next element covers it */
    margin-bottom: 0;

    ${atCardBottom && css`
      /* Lays the scrollbar along the card's bottom edge */
      margin-bottom: calc(2px - var(--card-content-padding, 0px));
    `}

    overflow-x: auto;
    overflow-y: hidden;

    -webkit-overflow-scrolling: touch !important;

    ${hoverScrollbar(theme)}

    @media (hover: hover) and (pointer: fine) {
      cursor: grab;

      /* The arrows sit outside the scroller, so hovering them should keep the bar */
      ${Viewport}:hover && {
        ${hoverScrollbarVisible(theme)}
      }
    }

    /* Fake padding to sides */
    &::before { content: ''; min-width: 1rem; }
    &::after { content: ''; min-width: 1rem; }

    /* Numbers below should be put somewhere globally */
    /* Quickfixes to avoid horizontal scrollers */
    ${isMobile && css`
      margin-left: -0.75rem;
      width: calc(100% + 1.5rem);
    `}

    /* Same as Card / CardContent */
    ${card && css`
      @media(max-width: 400px) {
        margin-left: -0.5rem;
        width: calc(100% + 1rem);
      }
    `}
  `}

  ${({ isDragging }) => isDragging && css`
    && { cursor: grabbing; }
    user-select: none;
  `}
`;

const Arrow = styled(IconButton, { shouldForwardProp: prop => prop !== 'side' })`
  position: absolute;
  top: calc(50% - 0.5rem);
  ${({ side }) => side}: -0.5rem;
  z-index: 1;
  width: 2rem;
  height: 2rem;
  transform: translateY(-50%);

  color: ${({ theme }) => theme.palette.text.primary};
  background: ${({ theme }) => theme.palette.background.paper};
  box-shadow: ${({ theme }) => theme.shadows[3]};

  transition: opacity 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  /* Larger hit area than the visible circle */
  &::before {
    content: '';
    position: absolute;
    inset: -0.75rem;
    border-radius: 50%;
  }

  &:hover {
    background: ${({ theme }) => theme.palette.background.paper};
    box-shadow: ${({ theme }) => theme.shadows[6]};
  }

  &.Mui-disabled {
    opacity: 0;
  }

  @media (hover: none), (pointer: coarse) {
    display: none;
  }
`;

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const HorizontalScroller = ({ card, atCardBottom, children, ...rest }) => {

  const { t } = useTranslation('component');

  const viewport = useRef();
  const scroller = useRef();
  const drag = useRef(null);
  const suppressClick = useRef(false);

  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const updateScrollState = useCallback(() => {
    if (!scroller.current) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = scroller.current;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  const updateLayoutState = useCallback(() => {
    if (!scroller.current) {
      return;
    }

    // Not scrollWidth: it counts children spilling past the last item, which the side padding absorbs once overflowing
    const { firstElementChild: first, lastElementChild: last } = scroller.current;
    const contentWidth = first ? last.getBoundingClientRect().right - first.getBoundingClientRect().left : 0;
    setHasOverflow(contentWidth > viewport.current.clientWidth + 1);

    updateScrollState();
  }, [updateScrollState]);

  useLayoutEffect(() => {
    updateLayoutState();
  }, [updateLayoutState, children]);

  useEffect(() => {
    const observer = new ResizeObserver(updateLayoutState);
    observer.observe(scroller.current);
    return () => observer.disconnect();
  }, [updateLayoutState]);

  const scrollByPage = direction => {
    scroller.current.scrollBy({
      left: direction * scroller.current.clientWidth * pageRatio,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  const onPointerDown = event => {
    suppressClick.current = false;
    const onScrollbar = event.clientY > scroller.current.getBoundingClientRect().top + scroller.current.clientHeight;
    if (!hasOverflow || onScrollbar || event.pointerType !== 'mouse' || event.button !== 0) {
      return;
    }

    drag.current = { x: event.clientX, scrollLeft: scroller.current.scrollLeft, moved: false };
  };

  const endDrag = () => {
    if (!drag.current) {
      return;
    }

    suppressClick.current = drag.current.moved;
    drag.current = null;
    setIsDragging(false);
  };

  const onPointerMove = event => {
    if (!drag.current) {
      return;
    }

    // The button can be released outside the row before the drag ever captured the pointer
    if (event.buttons === 0) {
      endDrag();
      return;
    }

    const distance = event.clientX - drag.current.x;
    if (!drag.current.moved) {
      if (Math.abs(distance) < dragThreshold) {
        return;
      }

      drag.current.moved = true;
      scroller.current.setPointerCapture(event.pointerId);
      setIsDragging(true);
    }

    scroller.current.scrollLeft = drag.current.scrollLeft - distance;
  };

  const onClickCapture = event => {
    if (suppressClick.current) {
      event.preventDefault();
      event.stopPropagation();
      suppressClick.current = false;
    }
  };

  const Outer = card ? Card : 'div';
  const Inner = card ? CardContent : React.Fragment;

  return (
    <Outer {...rest}>
      <Inner>
        <Viewport ref={viewport}>
          <ItemContainer
            ref={scroller}
            card={card}
            atCardBottom={card || atCardBottom}
            hasOverflow={hasOverflow}
            isDragging={isDragging}
            onScroll={updateScrollState}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onClickCapture={onClickCapture}
            onDragStart={event => hasOverflow && event.preventDefault()}
          >
            {children}
          </ItemContainer>
          {hasOverflow && (
            <>
              <Arrow side="left" size="small" disabled={!canScrollLeft} aria-label={t('horizontalScroller.scrollLeft')} onClick={() => scrollByPage(-1)}>
                <ChevronLeftRoundedIcon fontSize="small" />
              </Arrow>
              <Arrow side="right" size="small" disabled={!canScrollRight} aria-label={t('horizontalScroller.scrollRight')} onClick={() => scrollByPage(1)}>
                <ChevronRightRoundedIcon fontSize="small" />
              </Arrow>
            </>
          )}
        </Viewport>
      </Inner>
    </Outer>
  );
};

HorizontalScroller.propTypes = {
  card: PropTypes.bool,
  atCardBottom: PropTypes.bool,
};

export default HorizontalScroller;
